<template>
    <base-dialog :value="dialog" :width="600" :fullscreen="isMobile" :title="$t('exercises.select_statistics_exercises')"
        @input="val => { if (!val) hide() }" @close="hide">
        <div class="ese-selected">
            <div class="ese-sec-hdr">
                <span>{{ $t('exercises.selected_exercises') }}</span>
                <span class="ese-count" :class="{ full: exercises.length >= maxExercises }">
                    {{ exercises.length }}/{{ maxExercises }}
                </span>
            </div>
            <div v-if="exercises.length" class="ese-chips">
                <div v-for="ex in exercises" :key="ex.objectID" class="ese-chip">
                    <span class="ese-chip-name">{{ ex.name || '—' }}</span>
                    <v-icon size="14" class="ese-chip-x" @click="addExercise(ex)">mdi-close</v-icon>
                </div>
            </div>
            <div v-else class="ese-empty-sm">{{ $t('exercises.no_selected_exercises') }}</div>
        </div>

        <div class="ese-sec-hdr ese-sec-hdr--search">{{ $t('common.search') }}</div>
        <div class="ese-filters">
            <v-text-field ref="searchInput" :placeholder="$t('common.search')" append-icon="mdi-magnify"
                :loading="loadingSearch" @click:append="sendTextQuery" @keyup.enter="sendTextQuery" hide-details solo flat
                dense v-model="search" color="secondary" clearable @click:clear="search = ''" />
            <div class="ese-selects">
                <kimsa-combobox v-if="!limit" :placeholder="$t('exercises.pick_one_category')" dense solo
                    :items="categoriesOptions" @change="categories = $event" @clear="onClearCategories" placeholder-class
                    item-text="label" ref="categories" />
                <kimsa-combobox :placeholder="$t('exercises.pick_type')" dense solo :items="typesOptions"
                    item-text="label" @change="types = $event" @clear="onClearTypes" placeholder-class
                    multiple-with-chips :show-close-button="!!categories" ref="types" />
            </div>
        </div>

        <div v-if="exercisesOptions.length === 0" class="ese-empty">
            <span v-if="firstSearch">{{ $t('exercises.write_to_search_or_pick') }}</span>
            <span v-else>{{ $t('common.not_found') }}</span>
        </div>

        <v-virtual-scroll v-else style="overflow-x: hidden;" :bench="8" :items="exercisesOptions"
            :height="exercisesHeightContainer" item-height="70">
            <template v-slot:default="{ item }">
                <add-exercise-card :exercise="item" :added="isExerciseAdded(item.objectID)" is-second-dialog
                    @add="addExercise($event)" />
            </template>
        </v-virtual-scroll>

        <template v-slot:footer>
            <dialog-actions hide-cancel :confirm-label="$t('common.save')" :disabled="disabled" :loading="loadingSave"
                @confirm="saveExercises" />
        </template>
    </base-dialog>
</template>

<script>

import { mapActions, mapState } from 'vuex';
import { db } from "@/utils/Firebase";
import KimsaCombobox from "@/components/common/inputs/KimsaCombobox";
import AddExerciseCard from "@/components/coach/routines/AddExerciseCard";
import BaseDialog from "@/components/common/BaseDialog";
import DialogActions from "@/components/common/DialogActions";
import { ExerciseIndex } from "@/utils/Algolia";
import { debounce } from "@/utils/Common";

export default {
    name: "ExercisesSelectorStatisticsDialog",
    components: { AddExerciseCard, KimsaCombobox, BaseDialog, DialogActions },
    props: {
        coachId: {
            type: String,
            default: null,
        },
        limit: {
            type: String,
            default: null,
        }
    },
    data() {
        return {
            dialog: false,
            firstSearch: true,
            loadingSearch: false,
            loadingSave: false,
            showSearch: false,
            search: '',
            categories: [],
            types: [],
            exercises: [],
            exercisesOptions: [],
            type: '',
            maxExercises: 10,
        }
    },
    computed: {
        ...mapState(['categoriesOptions']),
        typesOptions(vm = this) {
            if (vm.categories === undefined || vm.categories.length === 0) return []
            return vm.categories.types.map(type => {
                return {
                    name: type,
                    value: type,
                    label: vm.typeLabel(type),
                }
            }).sort((a, b) => a.label.localeCompare(b.label))
        },
        exercisesHeightContainer(vm = this) {
            if (vm.exercisesOptions.length === 0) return 0
            if (vm.isMobile) return vm.$vuetify.breakpoint.height - 300
            return 320
        },
        disabled(vm = this) {
            return vm.exercises.length === 0
        }
    },
    async mounted() {
        const vm = this
        if (vm.categoriesOptions.length === 0) await vm.fetchExerciseCategories()
        if (vm.limit) vm.categories = vm.categoriesOptions.find(category => category.key === vm.limit)
        await vm.setDefaults()
    },
    methods: {
        ...mapActions(['fetchExerciseCategories']),
        show() {
            const vm = this
            vm.dialog = true
            vm.setDefaults()
        },
        hide() {
            const vm = this
            vm.dialog = false
            vm.reset()
            vm.$emit('hide')
        },
        reset() {
            const vm = this
            vm.search = ''
            vm.categories = []
            if (vm.dialog) {
                vm.$nextTick(() => {
                    vm.$refs.categories.model = ''
                    vm.$refs.types.model = ''
                })
            }
            vm.types = []
            vm.exercises = []
            vm.exercisesOptions = []
        },
        onTypesChange(types) {
            const vm = this
            vm.types = types
        },
        debouncedLoadExercises: debounce(
            function () {
                this.loadExercises()
            }, 500),
        sendTextQuery() {
            const vm = this
            if (!vm.search) return
            vm.loadExercises()
        },
        async loadExercises() {
            let vm = this
            vm.exercisesOptions = []
            vm.firstSearch = false
            let term = vm.search || ''
            let filter = `(coachId:"${vm.coachId}" OR owner:"app") AND hide!=1`
            vm.loadingSearch = true

            if (vm.types && vm.types.length > 0) {
                let types = vm.types.map(type => type.name)
                filter += ` AND (types:"${types.join('" OR types:"')}")`
            } else {
                // return console.log('Sin tipo seleccionado')
            }

            console.log('searching by:', term, filter)
            ExerciseIndex.search(term, {
                filters: filter,
                hitsPerPage: 50,
            })
                .then(({ hits }) => {
                    console.log('hits', hits)
                    vm.exercisesOptions = hits
                })
                .catch(err => {
                    console.log(err);
                })
                .finally(() => {
                    setTimeout(function () {
                        vm.loadingSearch = false
                    }, 600)
                });
        },
        addExercise(exercise) {
            const vm = this
            const index = vm.exercises.findIndex(ex => ex.objectID === exercise.objectID)
            if (index !== -1) {
                vm.exercises.splice(index, 1)
                return
            }
            if (vm.exercises.length >= vm.maxExercises) {
                return vm.toast(vm.$t('exercises.max_statistics_exercises'), 'error')
            }
            vm.exercises.push(exercise)
        },
        isExerciseAdded(id) {
            const vm = this
            let added = false
            vm.exercises.forEach((ex, i) => {
                if (ex.objectID === id) {
                    added = true
                }
            })
            return added
        },
        async setDefaults() {
            const vm = this
            if (!vm.coachId) return
            let coachRef = await db.collection('users').doc(vm.coachId).get()
            const ids = coachRef.data()?.statistics_exercises || []
            if (!ids.length) {
                vm.exercises = []
                return
            }
            try {
                const filter = ids.map(id => `objectID:"${id}"`).join(' OR ')
                const { hits } = await ExerciseIndex.search('', { filters: filter, hitsPerPage: ids.length })
                vm.exercises = ids.map(id => hits.find(hit => hit.objectID === id)).filter(Boolean)
            } catch (err) {
                console.log(err)
                vm.exercises = ids.map(id => ({ objectID: id }))
            }
        },
        onClearCategories() {
            const vm = this
            vm.types = []
            vm.debouncedLoadExercises()
        },
        onClearTypes() {
            const vm = this
            vm.types = []
        },
        async saveExercises() {
            const vm = this

            let exercises = vm.exercises.map(ex => {
                return ex.objectID
            })

            vm.loadingSave = true
            await db.collection('users').doc(vm.coachId).update({
                statistics_exercises: exercises
            })
            vm.$emit('saved')
            vm.toast(vm.$t('exercises.statistics_exercises_saved'))
            vm.loadingSave = false
            vm.hide()
        }
    },
    watch: {

        types(val) {
            const vm = this
            if (val && val.length > 0) {
                vm.debouncedLoadExercises()
            } else {
                vm.exercisesOptions = []
            }
        },
        search() {
            const vm = this
            vm.debouncedLoadExercises()
        },
        categories(val) {
            const vm = this
            if (vm.dialog) {
                vm.$nextTick(() => {
                    vm.$refs.types.model = ''
                })
            }
            vm.types = []
        },
        coachId() {
            this.setDefaults()
        }
    }
}

</script>

<style scoped>
.ese-sec-hdr {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 10px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: var(--v-txtMuted-base);
    margin-bottom: 10px;
}

.ese-sec-hdr--search {
    margin-top: 18px;
    padding-top: 16px;
    border-top: 1px solid var(--v-outline-base);
}

.ese-count {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: normal;
    color: var(--v-txtSecondary-base);
}

.ese-count.full {
    color: var(--v-primary-base);
}

.ese-selected {
    margin-bottom: 4px;
}

.ese-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.ese-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 30px;
    padding: 0 6px 0 12px;
    border-radius: 8px;
    background: rgba(102, 204, 204, 0.12);
    border: 1px solid var(--v-secondary-base);
    color: var(--v-txtPrimary-base);
    font-size: 12px;
    font-weight: 600;
    max-width: 100%;
}

.ese-chip-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.ese-chip-x {
    cursor: pointer;
    color: var(--v-txtMuted-base) !important;
    flex-shrink: 0;
}

.ese-chip-x:hover {
    color: var(--v-error-base) !important;
}

.ese-empty-sm {
    font-size: 12px;
    color: var(--v-txtMuted-base);
    padding: 8px 0;
}

.ese-filters {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-bottom: 12px;
}

.ese-selects {
    display: flex;
    gap: 10px;
}

.ese-selects > * {
    flex: 1;
    min-width: 0;
}

.ese-empty {
    text-align: center;
    padding: 32px 8px;
    color: var(--v-txtMuted-base);
    font-size: 13px;
}
</style>